import React, { useState } from "react";
import {
  Box,
  Typography,
  Modal,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import NewPhoneNumberComponent from "../new-phone-input-component/new-phone-input-component";
import DatePicker from "react-datepicker";
import CustomDateInput from "../custom-date-input-new/CustomDateInputNew";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar-icon-green.svg";
import { ReactComponent as ClockIcon } from "../../../assets/icons/clock-green.svg";
import { validationSchema } from "./validationSchema";
import { plannerBookConsultation } from "../../../api/preneed-planners.api";
import "react-datepicker/dist/react-datepicker.css";
import "./BookForConsultantPopup.scss";
interface BookForConsultantPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (formData: any) => void;
  plannerId: number;
}

const BookForConsultantPopup: React.FC<BookForConsultantPopupProps> = ({
  open,
  onClose,
  onSubmit,
  plannerId,
}) => {
  const [countryCode, setCountryCode] = useState<string>("+1");

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    bookingDate: null as Date | null,
    bookingTime: null as Date | null,
    preference: "",
    message: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const date = values?.bookingDate;
      const time = values?.bookingTime;

      const bookingDateTime = new Date(
        date?.getFullYear(),
        date?.getMonth(),
        date?.getDate(),
        time?.getHours(),
        time?.getMinutes(),
        0
      );

      // date & time as per API
      const formattedBookingDateTime =
        `${bookingDateTime.getFullYear()}-${(bookingDateTime.getMonth() + 1)
          .toString()
          .padStart(
            2,
            "0"
          )}-${bookingDateTime.getDate().toString().padStart(2, "0")} ` +
        `${bookingDateTime.getHours().toString().padStart(2, "0")}:${bookingDateTime.getMinutes().toString().padStart(2, "0")}:00`;

      // Build payload
      const submitValues = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: `${countryCode}${values.phoneNumber}`,
        bookingDateTime: formattedBookingDateTime,
        contactPreference: values.preference === "email" ? "Email" : "Call",
        message: values.message || "",
      };

      // Call API
      const success = await handlePlannerBookConsultation(submitValues);
      if (success && onSubmit) onSubmit(submitValues);
      formik.resetForm();
      onClose();
    },
  });

  const handlePlannerBookConsultation = async (formData: any) => {
    try {
      const response = await plannerBookConsultation(plannerId, formData);
      return response?.data?.success;
    } catch (error) {
      console.error("ERROR HANDLE PLANNER BOOK CONSULTATION::", error);
    }
  };

  const handlePreferenceSelect = (pref: string) => {
    formik.setFieldTouched("preference", true);
    formik.setFieldValue("preference", pref);
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleDateBlur = (field: "bookingDate" | "bookingTime") => {
    if (!formik.values[field]) {
      formik.setFieldTouched(field, true);
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleClose();
      }}
    >
      <Box className="book-consultant-popup">
        {/* Close Button */}
        <IconButton
          className="book-consultant-popup__close-btn"
          onClick={handleClose}
        >
          <CloseIcon fontSize="large" />
        </IconButton>

        {/* Header */}
        <Box className="book-consultant-popup__header">
          <Typography className="book-consultant-popup__title">
            Book Free Consultation
          </Typography>
          <Typography className="book-consultant-popup__subtitle">
            Use this form to book your free consultation
          </Typography>
        </Box>

        {/* Form */}
        <form
          className="book-consultant-popup__form"
          onSubmit={formik.handleSubmit}
        >
          {/* 2-column fields */}
          <Box className="book-consultant-popup__row">
            <Box className="book-consultant-popup__field">
              <Typography className="book-consultant-popup__label">
                First Name<span className="required">*</span>
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Enter First Name"
                required
                {...formik.getFieldProps("firstName")}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                onChange={(e) => {
                  const cleaned = e.target.value
                    .replace(/[^A-Za-z]/g, "")
                    .slice(0, 50);
                  formik.setFieldValue("firstName", cleaned);
                }}
                helperText={
                  formik.touched.firstName && formik.errors.firstName
                    ? formik.errors.firstName
                    : ""
                }
                className="book-consultant-popup__input"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "inherit !important",
                  },
                }}
              />
            </Box>

            <Box className="book-consultant-popup__field">
              <Typography className="book-consultant-popup__label">
                Last Name<span className="required">*</span>
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Enter Last Name"
                required
                {...formik.getFieldProps("lastName")}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                onChange={(e) => {
                  const cleaned = e.target.value
                    .replace(/[^A-Za-z]/g, "")
                    .slice(0, 50);
                  formik.setFieldValue("lastName", cleaned);
                }}
                helperText={
                  formik.touched.lastName && formik.errors.lastName
                    ? formik.errors.lastName
                    : ""
                }
                className="book-consultant-popup__input"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "inherit !important",
                  },
                }}
              />
            </Box>
          </Box>

          <Box className="book-consultant-popup__row">
            <Box className="book-consultant-popup__field">
              <Typography className="book-consultant-popup__label">
                Email ID<span className="required">*</span>
              </Typography>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Enter Email ID"
                required
                {...formik.getFieldProps("email")}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={
                  formik.touched.email && formik.errors.email
                    ? formik.errors.email
                    : ""
                }
                className="book-consultant-popup__input"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "inherit !important",
                  },
                }}
              />
            </Box>

            {/* Phone */}
            <Box className="book-consultant-popup__field">
              <Typography className="book-consultant-popup__label">
                Phone Number<span className="required">*</span>
              </Typography>
              <Box
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    height: "39px",
                    width: "100% !important",
                  },
                  "& .MuiInputBase-root": {
                    fontSize: "14px",
                    height: "40px",
                    padding: "12px 6px",
                  },
                  "& input": { color: "#104145" },
                }}
              >
                <NewPhoneNumberComponent
                  value={formik.values.phoneNumber}
                  onChange={(value: string) =>
                    formik.setFieldValue("phoneNumber", value)
                  }
                  countryCode={countryCode}
                  onCountryChange={setCountryCode}
                  placeholder="Enter Phone Number"
                  error={
                    formik.touched.phoneNumber &&
                    Boolean(formik.errors.phoneNumber)
                  }
                  showHelper={true}
                />
              </Box>
              {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                <Typography className="book-consultant-popup__error">
                  {formik.errors.phoneNumber}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="book-consultant-popup__row">
            <Box className="book-consultant-popup__field">
              <Typography className="book-consultant-popup__label">
                Booking Date
                <span className="required">*</span>
              </Typography>
              <DatePicker
                selected={
                  formik.values.bookingDate
                    ? new Date(formik.values.bookingDate)
                    : null
                }
                onChange={(date: Date | null) =>
                  formik.setFieldValue("bookingDate", date)
                }
                onCalendarClose={() => handleDateBlur("bookingDate")}
                dateFormat="MM/dd/yyyy"
                placeholderText="Select a Date"
                minDate={new Date()}
                customInput={
                  <CustomDateInput
                    placeholder="mm/dd/yyyy"
                    icon={<CalendarIcon />}
                    error={
                      formik.touched.bookingDate &&
                      Boolean(formik.errors.bookingDate)
                    }
                  />
                }
              />

              {formik.touched.bookingDate && formik.errors.bookingDate && (
                <Typography className="book-consultant-popup__error-date">
                  {formik.errors.bookingDate}
                </Typography>
              )}
            </Box>

            <Box className="book-consultant-popup__field">
              <Typography className="book-consultant-popup__label">
                Booking Time
                <span className="required">*</span>
              </Typography>
              <DatePicker
                selected={formik.values.bookingTime}
                onChange={(time) => formik.setFieldValue("bookingTime", time)}
                showTimeSelect
                showTimeSelectOnly
                onCalendarClose={() => handleDateBlur("bookingTime")}
                timeIntervals={5}
                timeCaption="Time"
                dateFormat="hh:mm aa"
                placeholderText="Select a Time slot"
                minTime={new Date()}
                maxTime={new Date(new Date().setHours(23, 55, 0, 0))}
                customInput={
                  <CustomDateInput
                    placeholder="hh:mm am"
                    icon={<ClockIcon />}
                    error={
                      formik.touched.bookingTime &&
                      Boolean(formik.errors.bookingTime)
                    }
                  />
                }
              />
              {formik.touched.bookingTime && formik.errors.bookingTime && (
                <Typography className="book-consultant-popup__error-date">
                  {formik.errors.bookingTime}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Preference Selection */}
          <Box className="book-consultant-popup__preference">
            <Typography className="book-consultant-popup__preference-label">
              Select Your Preference <span className="required">*</span>
            </Typography>
            <Box className="book-consultant-popup__preference-options">
              <Box
                className={`book-consultant-popup__preference-box ${
                  formik.values.preference === "email" ? "selected" : ""
                }`}
                onClick={() => handlePreferenceSelect("email")}
              >
                Through Email
              </Box>
              <Box
                className={`book-consultant-popup__preference-box ${
                  formik.values.preference === "call" ? "selected" : ""
                }`}
                onClick={() => handlePreferenceSelect("call")}
              >
                Through Call
              </Box>
            </Box>
            {formik.touched.preference && formik.errors.preference && (
              <Typography className="book-consultant-popup__error">
                {formik.errors.preference}
              </Typography>
            )}
          </Box>

          {/* Message */}
          <Box className="book-consultant-popup__field book-consultant-popup__field--full">
            <Typography className="book-consultant-popup__label">
              Message
            </Typography>
            <TextField
              multiline
              rows={2}
              placeholder="Tell us if you have any message to raise"
              fullWidth
              inputProps={{ maxLength: 200 }}
              {...formik.getFieldProps("message")}
              className="book-consultant-popup__message"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D9D9D9 !important",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "inherit !important",
                },
              }}
            />
          </Box>

          {/* Submit */}
          <Button
            type="submit"
            disabled={!formik.isValid || !formik.dirty}
            className="book-consultant-popup__submit-btn"
          >
            Book
          </Button>
        </form>
      </Box>
    </Modal>
  );
};

export default BookForConsultantPopup;
